"use client";

import { Dialog, DialogPanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from "clsx";
import { useEffect, useMemo, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { microHoverVariants } from '@/lib/animations';
import { setLanguageCookies, resolveLanguageFromPath, type Language } from '@/lib/language-client-utils';
import { sanitizeCmsHtml } from '@/lib/sanitize-cms-html';

interface LangSelectorClientProps {
    languages: Language[];
    defaultLanguageCode: string;
    initialSelectedLanguageCode?: string;
}

const REGION_LABELS: Record<string, string> = {
    europe: "Europe",
    asia: "Asia",
    "middle-east": "Middle East",
    africa: "Africa",
    "north-america": "North America",
    "south-america": "South America",
    oceania: "Oceania",
};

export default function LangSelectorClient({
                                               languages,
                                               defaultLanguageCode,
                                               initialSelectedLanguageCode,
                                           }: LangSelectorClientProps) {
    const router = useRouter();
    const pathname = usePathname();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const languageMap = useMemo(() => {
        const map = new Map<string, Language>();
        languages.forEach(lang => {
            map.set(lang.attributes.code, lang);
        });
        return map;
    }, [languages]);

    const topLanguages = useMemo(() => {
        return languages.slice(0, 5);
    }, [languages]);

    const groupedLanguages = useMemo(() => {
        const groups: Record<string, Language[]> = {};

        languages.forEach((lang) => {
            const region = lang.attributes.region || "other";
            if (!groups[region]) {
                groups[region] = [];
            }
            groups[region].push(lang);
        });

        return groups;
    }, [languages]);

    const orderedRegions = useMemo(() => {
        const preferredOrder = [
            "europe",
            "asia",
            "middle-east",
            "africa",
            "north-america",
            "south-america",
            "oceania",
            "other",
        ];

        return preferredOrder.filter(region => groupedLanguages[region]?.length > 0);
    }, [groupedLanguages]);

    const [selectedLangCode, setSelectedLangCode] = useState<string>(() => {
        return (
            resolveLanguageFromPath(pathname, languages) ||
            initialSelectedLanguageCode ||
            defaultLanguageCode
        );
    });

    const selectedLang =
        languageMap.get(selectedLangCode) ||
        languageMap.get(defaultLanguageCode) ||
        languages[0] ||
        null;

    useEffect(() => {
        const codeFromPath = resolveLanguageFromPath(pathname, languages);

        if (codeFromPath && codeFromPath !== selectedLangCode) {
            setSelectedLangCode(codeFromPath);
            return;
        }

        if (!codeFromPath && selectedLangCode !== defaultLanguageCode) {
            setSelectedLangCode(defaultLanguageCode);
        }
    }, [pathname, languages, selectedLangCode, defaultLanguageCode]);

    const handleLanguageChange = async (newLang: Language) => {
        const newLangCode = newLang.attributes.code;

        if (selectedLangCode === newLangCode) {
            setIsModalOpen(false);
            return;
        }

        const currentPath = pathname || '/';
        const pathSegments = currentPath.split('/').filter(Boolean);

        if (pathSegments[0] && /^[a-z]{2}$/.test(pathSegments[0])) {
            pathSegments.shift();
        }

        const pathWithoutLang = pathSegments.length > 0 ? '/' + pathSegments.join('/') : '';
        const pageSlug = pathSegments.length > 0 ? pathSegments[0] : '';

        setLanguageCookies(newLangCode);

        let targetUrl: string;

        if (pathSegments.length === 0) {
            targetUrl = newLang.attributes.is_default ? '/' : `/${newLangCode}`;
        } else {
            try {
                const params = new URLSearchParams({
                    slug: pageSlug,
                    current_language: selectedLangCode,
                    target_language: newLangCode,
                });

                const res = await fetch(`/api/alternate-url?${params.toString()}`);
                const data = await res.json();

                if (data?.url) {
                    targetUrl = data.url;
                } else {
                    targetUrl = newLang.attributes.is_default
                        ? pathWithoutLang
                        : `/${newLangCode}${pathWithoutLang}`;
                }
            } catch {
                targetUrl = newLang.attributes.is_default
                    ? pathWithoutLang
                    : `/${newLangCode}${pathWithoutLang}`;
            }
        }

        setSelectedLangCode(newLangCode);
        setIsModalOpen(false);
        router.push(targetUrl);
    };

    if (!languages || languages.length === 0 || !selectedLang) {
        return (
            <div className="text-white flex flex-row gap-2 sm:gap-3 lg:gap-4 items-center">
                <span className="font-medium">Language</span>
            </div>
        );
    }

    return (
        <>
            <Menu>
                {({ open }) => (
                    <>
                        <MenuButton className="text-white flex flex-row gap-2 sm:gap-3 lg:gap-4 items-center outline-0 cursor-pointer group">
                            <motion.div
                                className="flex flex-row gap-1 sm:gap-2 lg:gap-3 items-center"
                                variants={microHoverVariants}
                                initial="rest"
                                whileHover="hover"
                            >
                                {selectedLang.attributes.svg_code ? (
                                    <div
                                        className="w-full"
                                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(selectedLang.attributes.svg_code) }}
                                    />
                                ) : (
                                    <div className="w-6 h-4 bg-gray-300 rounded" />
                                )}
                                <span className="font-medium">
                                    {selectedLang.attributes.native_name || selectedLang.attributes.name}
                                </span>
                            </motion.div>

                            <motion.svg
                                width="8"
                                height="5"
                                viewBox="0 0 8 5"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                animate={{ rotate: open ? 180 : 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                                <path
                                    d="M7.87213 0.417894L7.61008 0.138679C7.52761 0.0514798 7.41788 0.00344123 7.30061 0.00344123C7.18341 0.00344123 7.07355 0.0514798 6.99108 0.138679L4.00179 3.30193L1.00905 0.135169C0.926707 0.0479698 0.816852 0 0.699648 0C0.582444 0 0.472524 0.0479698 0.390117 0.135169L0.128001 0.412663C-0.042667 0.593118 -0.042667 0.887061 0.128001 1.06752L3.69122 4.85148C3.77356 4.93861 3.88328 5 4.00153 5H4.00289C4.12016 5 4.22989 4.93854 4.31223 4.85148L7.87213 1.07777C7.9546 0.99064 7.99987 0.871025 8 0.747006C8 0.622918 7.9546 0.504955 7.87213 0.417894Z"
                                    fill="white"
                                />
                            </motion.svg>
                        </MenuButton>

                        <AnimatePresence>
                            {open && (
                                <MenuItems
                                    static
                                    as={motion.div}
                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                    anchor="bottom end"
                                    className="font-sans w-60 origin-top-right rounded-xl shadow-2xl bg-white mt-3 flex flex-col overflow-hidden focus:outline-none z-[9999] border border-gray-100"
                                >
                                    {topLanguages.map((lang, index) => (
                                        <MenuItem key={lang.attributes.code}>
                                            {({ focus }) => (
                                                <motion.button
                                                    type="button"
                                                    onClick={() => handleLanguageChange(lang)}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{
                                                        delay: index * 0.04,
                                                        duration: 0.2,
                                                        ease: "easeOut"
                                                    }}
                                                    className={clsx(
                                                        "w-full text-left transition-all duration-200 px-5 py-3.5 flex flex-row gap-3 items-center justify-between outline-0 relative group/item hover:pl-6",
                                                        focus ? "bg-alsero-first/10 text-alsero-first" : "text-alsero-dark-gray",
                                                        selectedLangCode === lang.attributes.code && "bg-alsero-first/5 font-semibold"
                                                    )}
                                                >
                                                    <div className="flex flex-row gap-3 items-center">
                                                        <div className="w-[35px]">
                                                            {lang.attributes.svg_code ? (
                                                                <div
                                                                    className="w-full"
                                                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(lang.attributes.svg_code) }}
                                                                />
                                                            ) : (
                                                                <div className="w-full h-5 bg-gray-300 rounded shadow-sm" />
                                                            )}
                                                        </div>
                                                        <span>{lang.attributes.native_name || lang.attributes.name}</span>
                                                    </div>

                                                    {selectedLangCode === lang.attributes.code && (
                                                        <motion.svg
                                                            initial={{ scale: 0, rotate: -180 }}
                                                            animate={{ scale: 1, rotate: 0 }}
                                                            transition={{ duration: 0.3, ease: "backOut" }}
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 16 16"
                                                            fill="none"
                                                            className="text-alsero-first"
                                                        >
                                                            <path
                                                                d="M13.3337 4L6.00033 11.3333L2.66699 8"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </motion.svg>
                                                    )}
                                                </motion.button>
                                            )}
                                        </MenuItem>
                                    ))}
                                    {topLanguages.length < languages.length && (
                                        <div className="border-t border-gray-100 px-3 py-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(true)}
                                            className="w-full rounded-lg px-4 py-2.5 text-sm font-medium text-alsero-first bg-alsero-first/5 hover:bg-alsero-first/10 transition"
                                        >
                                            More languages
                                        </button>
                                        </div>
                                    )}
                                </MenuItems>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </Menu>

            <AnimatePresence>
                {isModalOpen && (
                    <Dialog
                        open={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        className="relative z-[10000]"
                    >
                        <motion.div
                            className="fixed inset-0 bg-black/40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        />

                        <div className="fixed inset-0 flex items-center justify-center p-4">
                            <DialogPanel
                                as={motion.div}
                                initial={{ opacity: 0, y: 20, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 20, scale: 0.96 }}
                                className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden"
                            >
                                <div className="flex items-center justify-between px-6 py-4 border-b">
                                    <h2 className="text-xl font-semibold text-alsero-dark-gray">
                                        Select language
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="text-sm text-gray-500 hover:text-gray-900"
                                    >
                                        Close
                                    </button>
                                </div>

                                <div className="max-h-[70vh] overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {orderedRegions.map((region) => (
                                        <div key={region}>
                                            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                                                {REGION_LABELS[region] || region}
                                            </h3>

                                            <div className="space-y-2">
                                                {groupedLanguages[region].map((lang) => (
                                                    <button
                                                        key={lang.attributes.code}
                                                        type="button"
                                                        onClick={() => handleLanguageChange(lang)}
                                                        className={clsx(
                                                            "w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-left transition border",
                                                            selectedLangCode === lang.attributes.code
                                                                ? "border-alsero-first bg-alsero-first/5 text-alsero-first"
                                                                : "border-gray-100 hover:border-gray-200 hover:bg-gray-50 text-alsero-dark-gray"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8">
                                                                {lang.attributes.svg_code ? (
                                                                    <div
                                                                        className="w-full"
                                                                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(lang.attributes.svg_code) }}
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-5 bg-gray-300 rounded" />
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="font-medium">
                                                                    {lang.attributes.native_name || lang.attributes.name}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {lang.attributes.name}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {selectedLangCode === lang.attributes.code && (
                                                            <span className="text-xs font-semibold">
                                                                Selected
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </DialogPanel>
                        </div>
                    </Dialog>
                )}
            </AnimatePresence>
        </>
    );
}